import React, { useState } from 'react';
import ActivityPickerModal from './ActivityPickerModal';
import { createPlanningBulk } from '../../services/api';

// Obtiene nombre del día y fecha corta a partir de "YYYY-MM-DD" usando UTC para evitar timezone drift
function dayLabel(date) {
  // date puede ser un Date object (de getWeekDates) — lo convertimos a string ISO
  const iso = date instanceof Date ? date.toISOString().split('T')[0] : date;
  const [year, month, day] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  const nombre = d.toLocaleDateString('es-MX', { weekday: 'long', timeZone: 'UTC' });
  const corta = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  return {
    nombre: nombre.charAt(0).toUpperCase() + nombre.slice(1),
    corta: corta.replace('.', '').toUpperCase(),
  };
}

function getWeekDates(fromDate, toDate) {
  // Parsear como UTC para evitar que el timezone mueva el día
  const parseUTC = (str) => {
    if (!str) return new Date();
    const isISO = /^\d{4}-\d{2}-\d{2}/.test(str);
    if (isISO) {
      const [y, m, d] = str.split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d));
    }
    return new Date(str);
  };
  const start = parseUTC(fromDate);
  const end = parseUTC(toDate);
  const dates = [];
  const cur = new Date(start);
  while (cur <= end && dates.length < 7) {
    dates.push(new Date(cur));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return dates;
}

function formatDay(date) {
  // Genera "YYYY-MM-DD" en UTC
  return date.toISOString().split('T')[0];
}

export default function PlanningEditor({ config, onBack }) {
  const [rows, setRows] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const weekDates = getWeekDates(config.week.from_date, config.week.to_date);

  const addRow = (activity) => {
    const newRow = {
      id: Date.now(),
      activityId: activity.id,
      activityName: activity.name || activity.activity_name,
      days: weekDates.map(d => ({ date: formatDay(d), people: '' })),
    };
    setRows(r => [...r, newRow]);
    setShowPicker(false);
  };

  const updatePeople = (rowId, dayIdx, value) => {
    setRows(rows => rows.map(r =>
      r.id === rowId
        ? { ...r, days: r.days.map((d, i) => i === dayIdx ? { ...d, people: value } : d) }
        : r
    ));
  };

  const deleteRow = (rowId) => setRows(r => r.filter(x => x.id !== rowId));

  const totalForRow = (row) => row.days.reduce((sum, d) => sum + (parseFloat(d.people) || 0), 0);

  const handleSave = async () => {
    if (rows.length === 0) { setSaveError('Agrega al menos una actividad'); return; }
    setSaving(true);
    setSaveError('');
    try {
      const bulk = rows.map((row, idx) => ({
        activity_id: row.activityId,
        activity_sub_category_id: null,
        planning_id: null,
        priority: idx + 1,
        days: row.days.map(d => ({
          day: d.date,
          estimated_units: d.people ? String(parseFloat(d.people) * 8.25) : null,
          people_no: d.people || null,
          time: d.people ? '10' : null,
        })),
      }));
      const payload = {
        bulk,
        planning: {
          business_unit_id: String(config.businessUnitId),
          activity_area_id: config.areaId,
          stage_id: config.lot?.stage_id || config.lot?.id,
          week_id: config.weekId,
        },
      };
      await createPlanningBulk(payload);
      setSaveSuccess(true);
      setTimeout(() => { setSaveSuccess(false); onBack(); }, 1800);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Volver
        </button>
        <div style={styles.headerCenter}>
          <span style={styles.badge}>{config.businessUnit?.name}</span>
          <span style={styles.badge}>{config.area?.name}</span>
          <span style={styles.badge}>{config.lot?.name}</span>
          <span style={{ ...styles.badge, background: 'var(--accent-glow)', color: 'var(--accent)', border: '1px solid rgba(245,197,24,0.3)' }}>
            {config.week?.name}
          </span>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || rows.length === 0}
        >
          {saving ? (
            <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />Guardando...</>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
              Crear / Guardar semana de planeación
            </>
          )}
        </button>
      </div>

      {/* Alerts */}
      {saveError && (
        <div className="alert alert-error" style={{ margin: '0 32px' }}>{saveError}</div>
      )}
      {saveSuccess && (
        <div className="alert alert-success" style={{ margin: '0 32px' }}>
          ✓ Planeación guardada correctamente
        </div>
      )}

      {/* Table */}
      <div style={styles.tableWrap}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ width: 220, position: 'sticky', left: 0, background: 'var(--bg-primary)', zIndex: 2 }}>
                  Actividad
                </th>
                {weekDates.map((d, i) => {
                  const { nombre, corta } = dayLabel(d);
                  return (
                    <th key={i} style={{ textAlign: 'center', minWidth: 90 }}>
                      <div>{nombre}</div>
                      <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)', marginTop: 1 }}>
                        {corta}
                      </div>
                    </th>
                  );
                })}
                <th style={{ textAlign: 'center', minWidth: 90, color: 'var(--accent)' }}>Plan. semanal</th>
                <th style={{ width: 70 }}>Opciones</th>
              </tr>
            </thead>
            <tbody>
              {/* Add activity row */}
              <tr>
                <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowPicker(true)}
                    style={{ width: '100%' }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Agregar actividad
                  </button>
                </td>
                {weekDates.map((_, i) => <td key={i} />)}
                <td /><td />
              </tr>

              {/* Activity rows */}
              {rows.map(row => (
                <tr key={row.id}>
                  <td style={{
                    position: 'sticky', left: 0, background: '#fff', zIndex: 1,
                    fontWeight: 500, fontSize: 13,
                  }}>
                    {row.activityName}
                  </td>
                  {row.days.map((day, i) => (
                    <td key={i} style={{ padding: '6px 8px' }}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="input-field"
                        style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}
                        placeholder="—"
                        value={day.people}
                        onChange={e => updatePeople(row.id, i, e.target.value)}
                      />
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>
                    {totalForRow(row) || '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => deleteRow(row.id)}
                      title="Eliminar actividad"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={weekDates.length + 3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40, fontSize: 13 }}>
                    Haz clic en "Agregar actividad" para comenzar la planeación
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPicker && (
        <ActivityPickerModal
          config={config}
          onSelect={addRow}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: '24px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    flex: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '0 32px',
    flexWrap: 'wrap',
  },
  headerCenter: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    flex: 1,
    justifyContent: 'center',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    background: '#fff',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  },
  tableWrap: {
    margin: '0 32px',
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
  },
};
